import { ForecastMode } from "store/features/app/appSlice";

import { EntityKind } from "models/entityKind";

import {
  ChartQueryDependencies,
  useChartQueryDependencies
} from "./useChartQueryDependencies";
import {
  ChartRenderDependencies,
  useChartRenderDependencies
} from "./useChartRenderDependencies";

/**
 * @deprecated Use `useChartQueryDependencies` or `useChartRenderDependencies` directly.
 *
 * Backward-compatibility shim that merges both dependency hooks into a single
 * return value. Existing callers can continue to destructure from this hook
 * while they are migrated one at a time to the narrower hooks.
 *
 * Phase 2 of STATE_MANAGEMENT_MIGRATION_PLAN.md — Step 2.3.
 *
 * Removal deadline: set when all callers in Step 2.4 are updated.
 * Remaining callers at time of shim creation:
 *  - useChartDot
 *  - useChartSelections
 *  - useChartMouseInteractions
 *  - ChartWrapper
 */
export const useChartDependencies = (entityKind: EntityKind): ChartDependencies => {
  const queryDeps = useChartQueryDependencies(entityKind);
  const renderDeps = useChartRenderDependencies(entityKind);
  return { ...queryDeps, ...renderDeps };
};

export type ChartDependencies = ChartQueryDependencies & ChartRenderDependencies;

/**
 * Default / empty value for the combined chart dependencies.
 *
 * Used primarily by test utilities (e.g. `useChartDataControllerConsts.ts`)
 * to provide a base mock value that can be spread and selectively overridden.
 *
 * @deprecated Prefer `DEFAULT_QUERY_DEPENDENCIES` or `DEFAULT_RENDER_DEPENDENCIES`
 * once callers are migrated to the narrower hooks.
 */
export const DEFAULT_CHART_DEPENDENCIES: ChartDependencies = {
  // --- Query dependencies ---
  filterId: {
    id: "",
    lastUpdated: 0
  },
  sortBy: "AlphabeticalAsc",
  showGroupsNotInFilter: false,
  currentFilter: undefined,
  groupBy: undefined,
  hasCheckedBins: undefined,
  hasCheckedBinsFacility: undefined,
  placeholderBinsEnabled: undefined,
  globalNormalizeBy: undefined,
  useNormalizeBy: false,
  pdenDataSourceSetting: undefined,
  forecastMode: ForecastMode.Trump,
  userForecastSettings: undefined,
  useForecastStartDate: false,
  checkedGlobalForecasts: [],
  checkedGlobalTypeWells: [],
  syncWells: undefined,
  lockedColors: {},
  defaultSignificantFigures: undefined,
  useStrictRounding: undefined,

  // --- Render dependencies (Redux) ---
  normalizeTypeWell: false,
  activeColorPalette: undefined,
  colorPalettes: undefined,
  selectedGroups: [],
  selectedFacilityGroups: [],
  selectedWells: undefined,
  attentionWells: [],
  txnId: undefined,
  focusLegendItems: undefined,
  facilityFocusLegendItems: undefined,

  // --- Render dependencies (Jotai hover snapshots) ---
  hoverLegendItem: "",
  hoverLegendGroup: "",
  hoverLegendId: "",
  hoverLegendItemUnfiltered: "",
  hoverDataPoint: []
};
